import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ConversationList, { resolveTitle } from '../../components/chat/ConversationList';
import MessageTimeline from '../../components/chat/MessageTimeline';
import Composer from '../../components/chat/Composer';
import TypingIndicator from '../../components/chat/TypingIndicator';
import MessageSearchPanel from '../../components/chat/MessageSearchPanel';
import PinnedMessagesBar from '../../components/chat/PinnedMessagesBar';
import ConversationMuteMenu from '../../components/chat/ConversationMuteMenu';
import { useAuth } from '../../auth/AuthContext';
import { useChat } from '../../chat/ChatProvider';
import '../../styles/chat.css';

/** /chat — list (+ desktop empty pane). /chat/:id — room. */
export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { state, openConversation, myMemberId, loadConversations } = useChat();
  const [searchOpen, setSearchOpen] = useState(false);
  // Keep as string — snowflake IDs exceed Number.MAX_SAFE_INTEGER
  const id = conversationId != null && conversationId !== '' ? conversationId : null;
  const hasRoom = id != null && String(id) !== '';

  useEffect(() => {
    setSearchOpen(false);
  }, [id]);

  useEffect(() => {
    if (hasRoom) {
      openConversation(id);
      refreshUser().catch(() => {});
    }
  }, [hasRoom, id, openConversation, refreshUser]);

  const conv = state.conversations.find(
    (c) => String(c.conversationId || c.id) === String(id)
  );
  const baseTitle = conv
    ? resolveTitle(conv, state.participantSummaries, myMemberId)
    : hasRoom
      ? `會話 ${id}`
      : '聊天';
  const isGroup =
    conv?.conversationType === 'GROUP' || conv?.conversationType === 'BUSINESS_GROUP';
  const memberCount = (conv?.participantMemberIds || []).length;
  const title =
    isGroup && memberCount > 0 ? `${baseTitle}(${memberCount})` : baseTitle;

  const socketLabel = {
    open: '已連線',
    connecting: '連線中…',
    reconnecting: '重連中…',
    disconnected: '已斷線',
    idle: '未連線',
  }[state.socketStatus] || state.socketStatus;

  return (
    <div className={`chat-app ${hasRoom ? 'has-room' : 'list-only'}`}>
      <aside className="chat-app__sidebar">
        <header className="chat-app__side-header">
          <h1>聊天</h1>
          <div className="chat-app__status" data-status={state.socketStatus}>
            {socketLabel}
          </div>
          <button type="button" className="chat-link-btn" onClick={() => loadConversations()}>
            重新整理
          </button>
          <Link to="/chat/contacts" className="chat-link-btn">
            聯絡人
          </Link>
          <Link to="/chat/favorites" className="chat-link-btn">
            收藏
          </Link>
          <Link to="/chat/groups" className="chat-link-btn">
            群組
          </Link>
          <Link to="/chat/groups/new" className="chat-link-btn">
            建群
          </Link>
        </header>
        <ConversationList />
      </aside>

      <section className="chat-app__main">
        {hasRoom ? (
          <>
            <header className="chat-app__room-header">
              <button
                type="button"
                className="chat-app__back"
                onClick={() => navigate('/chat')}
                aria-label="返回列表"
              >
                ←
              </button>
              <div className="chat-app__room-title">
                <strong>{title}</strong>
                <small>{conv?.conversationType || ''}</small>
              </div>
              {conv?.conversationType === 'GROUP' || conv?.conversationType === 'BUSINESS_GROUP' ? (
                <Link to={`/chat/groups/${id}/add`} className="chat-link-btn">
                  加人
                </Link>
              ) : null}
              {conv?.conversationType === 'DIRECT' && conv?.peerMemberId != null ? (
                <Link
                  to={`/chat/report?memberId=${encodeURIComponent(conv.peerMemberId)}&conversationId=${encodeURIComponent(id)}`}
                  className="chat-link-btn"
                >
                  檢舉
                </Link>
              ) : null}
              <button
                type="button"
                className="chat-link-btn"
                onClick={() => setSearchOpen((v) => !v)}
              >
                搜尋
              </button>
              <ConversationMuteMenu conversationId={id} conversation={conv} />
              <Link to="/messages" className="chat-link-btn">
                站內信
              </Link>
            </header>
            {searchOpen ? (
              <MessageSearchPanel
                conversationId={id}
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
              />
            ) : null}
            <PinnedMessagesBar conversationId={id} />
            {state.socketStatus === 'disconnected' || state.socketStatus === 'reconnecting' ? (
              <div className="chat-banner">即時連線中斷，正在嘗試重連；訊息仍可透過 REST 補拉</div>
            ) : null}
            <MessageTimeline conversationId={id} />
            <TypingIndicator conversationId={id} />
            <Composer conversationId={id} />
          </>
        ) : (
          <div className="chat-app__empty-pane">
            <p>選擇左側會話開始聊天</p>
            <p className="chat-state__hint">手機請從列表點入會話</p>
          </div>
        )}
      </section>

      <aside className="chat-app__info" aria-hidden={!hasRoom}>
        {hasRoom ? (
          <div className="chat-info-panel">
            <h2>會話資訊</h2>
            <p>ID：{id}</p>
            <p>類型：{conv?.conversationType || '—'}</p>
            <p>未讀：{conv?.unreadCount ?? 0}</p>
            <p className="chat-state__hint">右側資訊欄預留（成員／公告等）</p>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
